import { forwardRef, useImperativeHandle, useRef, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faSpinner } from '@fortawesome/pro-light-svg-icons';
import { CRMTag } from '../../models/CRM';
import { useToast } from '@shared/hooks/use-toast';
import { useTags, useModifyOrderTags } from '../../hooks/useTags';
import { useCanManageCRM } from '@modules/CRM/shared/hooks/useCRMSettings';
import { getTextColorForBackground } from '../../utils/colorUtils';

export interface TagsModalRef {
  open: (orderId: number, orderRef: string, tags: CRMTag[]) => void;
}

interface TagsModalProps {
  onSuccess?: () => void;
}

/**
 * Modal para gestionar etiquetas asignadas a un pedido.
 * Desplegable con lista de tags desde GET /crm/tags. Al añadir: POST /crm/orders/tags/modify (attach_tags).
 * Los tags ya asignados al pedido vienen de order.tags (lista CRM/orders). Quitar: detach_tags.
 */
export const TagsModal = forwardRef<TagsModalRef, TagsModalProps>(({ onSuccess }, ref) => {
  const { t } = useTranslation('crmModule');
  const { toast } = useToast();
  const modalRef = useRef<ModalCenterRef>(null);
  const removeTagConfirmRef = useRef<ConfirmModalRef>(null);
  const [orderId, setOrderId] = useState<number | undefined>(undefined);
  const [tags, setTags] = useState<CRMTag[]>([]);
  const [selectedTagId, setSelectedTagId] = useState('');
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [addingTagName, setAddingTagName] = useState<string | null>(null);
  const [removingTagId, setRemovingTagId] = useState<number | null>(null);

  const { tags: availableTags, isLoading: isLoadingTags } = useTags({ per_page: 100 }, !!orderId);
  const modifyOrderTagsMutation = useModifyOrderTags();
  const { canManage } = useCanManageCRM();

  const isSubmitting = modifyOrderTagsMutation.isPending;

  const tagOptions = useMemo(() => {
    const assignedIds = new Set(tags.map((tag) => tag.id));
    return availableTags
      .filter((tag) => !assignedIds.has(tag.id))
      .map((tag) => ({
        label: tag.name,
        value: String(tag.id),
        color: tag.color, // Incluir el color para mostrar badges en el listado
      }));
  }, [availableTags, tags]);

  useImperativeHandle(ref, () => ({
    open: (orderIdParam: number, _orderRef: string, initialTags: CRMTag[]) => {
      setOrderId(orderIdParam);
      setTags(initialTags ?? []);
      setSelectedTagId('');
      setIsAddingTag(false);
      setAddingTagName(null);
      setRemovingTagId(null);
      modalRef.current?.openModal();
    },
  }));

  const handleAddTag = useCallback(
    async (tagIdValue: string) => {
      const tagId = tagIdValue ? Number(tagIdValue) : 0;
      if (!tagId || orderId === undefined || isSubmitting || isAddingTag) return;

      const tagToAdd = availableTags.find((t) => t.id === tagId);
      if (!tagToAdd) return;

      // Activar estado de carga inmediatamente
      setIsAddingTag(true);
      setAddingTagName(tagToAdd.name);
      setSelectedTagId('');

      try {
        await modifyOrderTagsMutation.mutateAsync({
          order_id: orderId,
          attach_tags: [tagId],
        });

        setTags((prev) => [...prev, { id: tagToAdd.id, name: tagToAdd.name, color: tagToAdd.color }]);
        setIsAddingTag(false);
        setAddingTagName(null);
        onSuccess?.();
        toast({
          title: t('tags.addSuccess.title', 'Etiqueta añadida'),
          description: t('tags.addSuccess.description', 'La etiqueta se ha asignado al pedido.'),
          variant: 'default',
        });
      } catch (error: any) {
        setIsAddingTag(false);
        setAddingTagName(null);
        toast({
          title: t('tags.addError.title', 'Error al añadir etiqueta'),
          description:
            error?.response?.data?.message || error?.message || t('tags.addError.description', 'Error inesperado.'),
          variant: 'destructive',
        });
      }
    },
    [orderId, availableTags, modifyOrderTagsMutation, onSuccess, t, toast, isSubmitting, isAddingTag],
  );

  // Función wrapper para manejar la selección del Combobox
  const handleTagSelection = useCallback(
    (value: string) => {
      if (value) {
        setSelectedTagId(value);
        // Ejecutar la lógica de añadir directamente con el valor seleccionado
        handleAddTag(value);
      }
    },
    [handleAddTag],
  );

  const performRemoveTag = useCallback(
    async (tagId: number) => {
      if (orderId === undefined) return;

      setRemovingTagId(tagId);
      try {
        await modifyOrderTagsMutation.mutateAsync({
          order_id: orderId,
          detach_tags: [tagId],
        });
        setTags((prev) => prev.filter((t) => t.id !== tagId));
        onSuccess?.();
        toast({
          title: t('tags.removeSuccess.title', 'Etiqueta eliminada'),
          description: t('tags.removeSuccess.description', 'La etiqueta se ha quitado del pedido.'),
          variant: 'default',
        });
      } catch (error: any) {
        toast({
          title: t('tags.removeError.title', 'Error al eliminar etiqueta'),
          description:
            error?.response?.data?.message || error?.message || t('tags.removeError.description', 'Error inesperado.'),
          variant: 'destructive',
        });
      } finally {
        setRemovingTagId(null);
      }
    },
    [orderId, modifyOrderTagsMutation, onSuccess, t, toast],
  );

  const handleRemoveTagClick = useCallback(
    (tag: CRMTag) => {
      removeTagConfirmRef.current?.open(
        async () => {
          await performRemoveTag(tag.id);
        },
        undefined,
        t('tags.removeConfirm.title', 'Desasignar etiqueta'),
        t('tags.removeConfirm.message', '¿Desasignar la etiqueta del pedido?').replace('{{name}}', tag.name),
      );
    },
    [performRemoveTag, t],
  );

  return (
    <ModalCenter
      ref={modalRef}
      title={t('tags.modal.title', 'Etiquetas asignadas')}
      type="default"
      width="default"
      className="w-full sm:w-[500px] max-h-[90vh]"
    >
      {/* Desplegable de tags (lista desde GET /crm/tags) - se asigna automáticamente al seleccionar */}
      <div className="mb-4">
        <Combobox
          options={tagOptions}
          value={selectedTagId}
          setValue={handleTagSelection}
          placeholder={t('tags.selectPlaceholder', 'Seleccionar etiqueta')}
          className="w-full"
          disabled={!canManage || isSubmitting || isLoadingTags || isAddingTag}
          dataTestId="tags-combobox"
        />
      </div>

      {/* Lista de etiquetas con X para eliminar - Tags list with remove */}
      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => (
          <span
            key={tag.id}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-0.5 text-sm border"
            style={
              tag.color
                ? {
                    backgroundColor: tag.color,
                    color: getTextColorForBackground(tag.color),
                    borderColor: tag.color,
                  }
                : {
                    backgroundColor: 'var(--wrh-card-border)',
                    color: 'var(--wrh-text)',
                    borderColor: 'var(--wrh-card-border)',
                  }
            }
          >
            <span>{tag.name}</span>
            {removingTagId === tag.id ? (
              <span className="flex items-center justify-center w-5 h-5" aria-hidden>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="w-3 h-3 animate-spin"
                  style={{ color: tag.color ? getTextColorForBackground(tag.color) : 'var(--wrh-text)' }}
                />
              </span>
            ) : (
              <button
                type="button"
                onClick={() => handleRemoveTagClick(tag)}
                disabled={!canManage || isSubmitting || isAddingTag}
                className="flex items-center justify-center w-5 h-5 rounded-full hover:bg-hover-press transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`remove-tag-${tag.id}`}
                aria-label={t('tags.remove', 'Eliminar etiqueta')}
              >
                <FontAwesomeIcon
                  icon={faXmark}
                  className="w-3 h-3"
                  style={{ color: tag.color ? getTextColorForBackground(tag.color) : 'var(--wrh-text)' }}
                />
              </button>
            )}
          </span>
        ))}
        {/* Tag en proceso de añadirse */}
        {isAddingTag && addingTagName && (
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-0.5 text-sm text-text bg-card-border border border-card-border opacity-60">
            <FontAwesomeIcon icon={faSpinner} className="w-3 h-3 animate-spin text-text" />
            <span>{addingTagName}</span>
          </span>
        )}
      </div>

      {tags.length === 0 && !isAddingTag && (
        <p className="text-sm text-complementary-text mt-2">
          {t('tags.empty', 'No hay etiquetas asignadas. Añade una con el campo de arriba.')}
        </p>
      )}
      <ConfirmModal ref={removeTagConfirmRef} />
    </ModalCenter>
  );
});

TagsModal.displayName = 'TagsModal';
